/**
 * The ad's page once the brief is final (docs/SPEC.md §6.1, §7.5, §7.6, §8,
 * §10, §14.1; P28-T09): one page from version 1's writing on, while it builds
 * and once it is ready. The player of `frontend/src/film/` reads the current
 * version's manifest (`version.manifest_url`), the scene strip under it, the
 * conversation beside it, the cast panel's place, the brief as a fold, the
 * versions, and what was made from the film.
 *
 * - **Building** (§6.1, §6.2): "Writing the film…" until version 1 is
 *   written, then "Building scene 2 of 4…". The manifest of a building
 *   version lists the scenes baked so far (`scenes[k].ready`); each
 *   `progress` event that names a scene reads it again. The player opens on
 *   the first scene to be ready and plays it alone, looping; a ready tile
 *   picked in the strip plays that scene; a tile not built yet is grey. When
 *   every scene is ready the whole film plays.
 * - **The strip** is the scrubber's scenes (§7.5): each tile a frame the
 *   player drew in the browser (`film/frames.ts::frameOf`), never a picture
 *   the server made. A tap focuses that scene (it loops) and tells the
 *   server (`POST …/focus`); the film's name leaves.
 * - **Looking around** (§7.6): while the film plays a drag on the picture
 *   turns the view (Shift or a second button slides it, the wheel comes
 *   nearer); paused, **Look around** does the same; **Back to the film's
 *   camera** returns.
 * - **Pointing** (§8.2), paused: a click is the object under the pointer
 *   (the scene's own nodes, named by the sidecar), a drag an area.
 * - **A change** carries §8.2's body: the moment, the focus, the object, the
 *   area, the view when the person looked around, the marked part, and the
 *   picture (a JPEG of the canvas, ≤ 200 kB, the pointing drawn on it).
 *   A dragged segment sends "Move scene 3 before scene 1" the same way. The
 *   box is there from version 1's writing on; a change sent while the film
 *   builds waits for it, and the box says so.
 * - **The brief** (§5.5): a fold, closed by default; an edit or an Accept
 *   there is a change of the film written by code (`PATCH …/brief`, 202).
 * - **The socket**: an applied instant change's `patches` go to the player
 *   at once (`applyPatches`) and its `film` gives the locks and names; its
 *   `version` event brings the new manifest; `progress` the box's fraction
 *   and the scene just built.
 * - **Compare** (§10): the player split at the same moment between two
 *   versions (scenes matched by id, then by name; a scene missing on the
 *   other side shows grey).
 * - **Approve** (§13, P24-T05) opens the three ways out (`WaysOut`) once the
 *   film is ready; the outputs made so far are listed under the player
 *   (`Outputs`), with the true render of a moment (`TrueRenderPanel`, also
 *   "See one moment first"). The brand kit's line (§14.1) is the page
 *   heading's (AdPage).
 * - **The cast panel** (§6.3, P28-T14's `CastPanel`): the page gives it its
 *   place (`cast`), beside the conversation, and the strip's frames (a
 *   stand-in's picture is its first scene's tile).
 */
import { useQuery } from "@tanstack/react-query";
import {
  type PointerEvent,
  type ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { AdDetail, Brief } from "../api/ads";
import { useAcceptSuggestion, useEditBrief } from "../api/brief";
import { useCast } from "../api/cast";
import { priceAsk, shortfallOf } from "../api/client";
import {
  type Change,
  type ChangeAnswer,
  type ChangeBody,
  RUNNING,
  type Span,
  useAnswerChange,
  useAskChange,
  useCancelChange,
  useChanges,
  useConfirmChange,
} from "../api/changes";
import { useTemplates } from "../api/templates";
import {
  type FilmDoc,
  manifestUrlOf,
  useFocus,
  useManifest,
  useRestore,
  useVersion,
  useVersions,
} from "../api/versions";
import {
  BOX,
  BUILD,
  COMPARE,
  FILM_STATE,
  LOOK,
  SCRUB,
  changeError,
  fillAll,
  secondsText,
} from "../copy/changes";
import { sentenceFor } from "../copy/errors";
import { LazyFilmPlayer } from "../film/lazy";
import {
  FREE_ZERO,
  type FreeOffset,
  type Rect,
  areaOf,
  changeView,
  dollyStep,
  orbitStep,
  panStep,
  rectBetween,
} from "../film/look";
import { type Manifest, allReady } from "../film/manifest";
import type { Patch } from "../film/patches";
import type { PictureMark } from "../film/picture";
import type { FilmPlayerHandle, PlayerClock } from "../film/Player";
import type { SocketEvent } from "../ui/useSocket";
import { Button, Card, Skeleton } from "../ui";
import { BriefCard, refNames } from "./BriefCard";
import { CastFilmContext } from "./CastPanel";
import { ChangeBox, type PointingChip } from "./ChangeBox";
import { templateCardOf } from "./TemplateCard";
import { Scrubber } from "./Scrubber";
import { type ScrubScene, scrubScenes } from "./scrub";
import { type AdProgress, progressKey } from "./useAdSocket";
import { useSceneFrames } from "./useSceneFrames";
import { VersionsTree } from "./VersionsTree";
import { Outputs } from "./Outputs";
import { ShareCard } from "./SharePanel";
import { type MomentAsked, TrueRenderPanel } from "./TrueRenderPanel";
import { WaysOut } from "./WaysOut";
import { WAYS_OUT } from "../copy/outputs";

export type AdListener = (event: SocketEvent) => void;
export interface AdBus {
  add(listener: AdListener): () => void;
}

const say = (s: string, p: Record<string, unknown>) => fillAll(s, p) ?? s;

const round2 = (x: number) => Math.round(x * 100) / 100 + 0;

/** How far a press travels before it is a drag, CSS pixels. */
const DRAG_PX = 5;

interface Press {
  x: number;
  y: number;
  look: boolean;
  pan: boolean;
  from: FreeOffset;
  moved: boolean;
}

/** The layer over the picture: looking around and pointing. */
function PictureLayer({
  playing,
  lookMode,
  area,
  mark,
  onLook,
  onPan,
  onDolly,
  onArea,
  onClick,
  offsetNow,
}: {
  playing: boolean;
  lookMode: boolean;
  area: Rect | null;
  mark: [number, number] | null;
  onLook(dx: number, dy: number, h: number, from: FreeOffset): void;
  onPan(dx: number, dy: number, h: number, from: FreeOffset): void;
  onDolly(factor: number): void;
  onArea(rect: Rect): void;
  onClick(x: number, y: number): void;
  /** The person's view now (a drag starts from it). */
  offsetNow(): FreeOffset;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const press = useRef<Press | null>(null);
  const [draft, setDraft] = useState<Rect | null>(null);
  const looking = playing || lookMode;

  // the wheel comes nearer; React's own listener is passive
  const dollyRef = useRef(onDolly);
  dollyRef.current = onDolly;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      dollyRef.current(e.deltaY > 0 ? 1.1 : 1 / 1.1);
    };
    el.addEventListener("wheel", wheel, { passive: false });
    return () => el.removeEventListener("wheel", wheel);
  }, []);

  const local = (e: PointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return {
      x: e.clientX - r.left,
      y: e.clientY - r.top,
      w: r.width,
      h: r.height,
    };
  };

  return (
    <div
      ref={ref}
      data-testid="picture-layer"
      role="application"
      aria-label={say(LOOK.picture, {
        hint: looking ? LOOK.hintLooking : LOOK.hintPaused,
      })}
      className={`absolute inset-0 touch-none select-none ${looking ? "cursor-grab" : "cursor-crosshair"}`}
      onContextMenu={(e) => e.preventDefault()}
      onPointerDown={(e) => {
        const p = local(e);
        press.current = {
          x: p.x,
          y: p.y,
          look: looking,
          pan: e.shiftKey || e.button === 2 || e.button === 1,
          from: offsetNow(),
          moved: false,
        };
        try {
          e.currentTarget.setPointerCapture?.(e.pointerId);
        } catch {
          // capture is a nicety
        }
      }}
      onPointerMove={(e) => {
        const s = press.current;
        if (!s) return;
        const p = local(e);
        const dx = p.x - s.x;
        const dy = p.y - s.y;
        if (!s.moved && Math.hypot(dx, dy) < DRAG_PX) return;
        s.moved = true;
        if (s.look) {
          if (s.pan) onPan(dx, dy, p.h, s.from);
          else onLook(dx, dy, p.h, s.from);
          return;
        }
        setDraft([
          Math.min(s.x, p.x) / p.w,
          Math.min(s.y, p.y) / p.h,
          Math.max(s.x, p.x) / p.w,
          Math.max(s.y, p.y) / p.h,
        ]);
      }}
      onPointerUp={(e) => {
        const s = press.current;
        press.current = null;
        setDraft(null);
        if (!s || s.look) return;
        const p = local(e);
        if (s.moved) {
          const rect = rectBetween([s.x, s.y], [p.x, p.y], p.w, p.h);
          if (rect) onArea(rect);
          return;
        }
        onClick(p.x / Math.max(1, p.w), p.y / Math.max(1, p.h));
      }}
      onPointerCancel={() => {
        press.current = null;
        setDraft(null);
      }}
    >
      {(draft ?? (!playing ? area : null)) ? (
        <span
          aria-hidden="true"
          data-testid="area-outline"
          className="pointer-events-none absolute border-2 border-pink-500 bg-pink-500/10"
          style={boxStyle(draft ?? area!)}
        />
      ) : null}
      {mark && !playing && !area ? (
        <span
          aria-hidden="true"
          data-testid="point-mark"
          className="pointer-events-none absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-pink-500 bg-white/40"
          style={{ left: `${mark[0] * 100}%`, top: `${mark[1] * 100}%` }}
        />
      ) : null}
    </div>
  );
}

function boxStyle(r: Rect) {
  return {
    left: `${r[0] * 100}%`,
    top: `${r[1] * 100}%`,
    width: `${(r[2] - r[0]) * 100}%`,
    height: `${(r[3] - r[1]) * 100}%`,
  };
}

/** The scene of `other` at the moment `{scene, t}` of `main`: by id, then by
 *  name; null when the other film lacks it. */
export function matchScene(
  main: Manifest,
  other: Manifest,
  sceneId: string,
): Manifest["scenes"][number] | null {
  const own = main.scenes.find((s) => s.id === sceneId);
  return (
    other.scenes.find((s) => s.id === sceneId) ??
    (own ? other.scenes.find((s) => s.name === own.name) : undefined) ??
    null
  );
}

/** How often the manifest of a film being built is read again when no
 *  socket event says a scene is ready (the socket may be closed). */
export const MANIFEST_POLL_MS = 5_000;

/** A tile's picture, CSS pixels on its long side (drawn at twice that). */
const TILE_PX = 160;

const RATIOS: Record<string, [number, number]> = {
  "9:16": [9, 16],
  "1:1": [1, 1],
  "16:9": [16, 9],
};
const ratioOf = (aspect: string): [number, number] =>
  RATIOS[aspect] ?? RATIOS["9:16"];

/** The scenes of a film not yet written or with no scene built: what the
 *  version line (or the brief) says, laid end to end. */
function plannedScenes(
  scenes: { id: string; name: string; seconds: number }[],
): ScrubScene[] {
  let start = 0;
  return scenes.map((s) => {
    const out = {
      id: s.id,
      name: s.name,
      seconds: s.seconds,
      start,
      end: start + s.seconds,
      locked: false,
      ready: false,
    };
    start += s.seconds;
    return out;
  });
}

/** The strip before any scene can play: a grey tile per scene. */
function PlannedStrip({ scenes }: { scenes: ScrubScene[] }) {
  if (scenes.length === 0) return null;
  return (
    <ol
      aria-label={SCRUB.scenes}
      className="m-0 flex w-full list-none gap-1 p-0"
      data-testid="planned-strip"
    >
      {scenes.map((scene, i) => (
        <li
          key={scene.id}
          style={{ flexGrow: scene.seconds, flexBasis: 0 }}
          className="flex min-w-0 flex-col gap-1"
          data-testid={`segment-${scene.id}`}
          data-ready="false"
        >
          <span
            aria-hidden="true"
            className="block h-14 w-full animate-pulse rounded-md bg-slate-200 md:h-20"
            data-testid={`tile-${scene.id}`}
          />
          <span className="flex min-w-0 items-center gap-1 px-1 text-xs text-slate-500">
            <span className="shrink-0 font-semibold tabular-nums">{i + 1}</span>
            <span className="truncate">{scene.name}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

export interface FilmPageProps {
  ad: AdDetail;
  bus: AdBus;
  /** The cast panel (§6.3, P28-T14), beside the conversation. */
  cast?: ReactNode;
}

export function FilmPage({ ad, bus, cast }: FilmPageProps) {
  const current = ad.current_version;
  /** Version 1 is being written or built (a later version builds behind the
   *  one that plays). */
  const building = current?.state !== "ready";
  const manifestQ = useManifest(
    current?.manifest_url ?? null,
    building ? MANIFEST_POLL_MS : false,
  );
  const versionQ = useVersion(ad.id, current?.n ?? null);
  const versionsQ = useVersions(ad.id);
  const changesQ = useChanges(ad.id);
  const progressQ = useQuery<AdProgress | null>({
    queryKey: progressKey(ad.id),
    queryFn: () => null,
    enabled: false,
  });
  const ask = useAskChange(ad.id);
  const answer = useAnswerChange(ad.id);
  const confirm = useConfirmChange(ad.id);
  const cancel = useCancelChange(ad.id);
  const restore = useRestore(ad.id);
  const focusQ = useFocus(ad.id);
  const edit = useEditBrief(ad.id);
  const accept = useAcceptSuggestion(ad.id);

  const playerRef = useRef<FilmPlayerHandle | null>(null);
  const otherRef = useRef<FilmPlayerHandle | null>(null);
  const [clock, setClock] = useState<PlayerClock>({
    playing: false,
    focus: ad.focus_scene,
    filmTime: 0,
    t: 0,
  });
  const [offset, setOffsetState] = useState<FreeOffset | null>(null);
  /** The view as the last drag left it (a drag reads it at its press). */
  const offsetRef = useRef<FreeOffset | null>(null);
  const [lookMode, setLookMode] = useState(false);
  const [object, setObject] = useState<string | null>(null);
  const [mark, setMark] = useState<[number, number] | null>(null);
  const [area, setArea] = useState<Rect | null>(null);
  const [span, setSpan] = useState<Span | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [comparing, setComparing] = useState<number | null>(null);
  const [missing, setMissing] = useState<string | null>(null);
  const [eventFilm, setEventFilm] = useState<FilmDoc | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [waysOpen, setWaysOpen] = useState(false);
  const [asking, setAsking] = useState<MomentAsked | null>(null);

  // ---- the manifest: the one read last, and the one the player holds -------
  /** The manifest as last read: which scenes are built. */
  const latest = manifestQ.data ?? null;
  /** Every scene is built: the whole film plays. */
  const whole = !!latest && allReady(latest);
  /** The scene picked in the strip while the film builds. */
  const [pick, setPick] = useState<string | null>(null);
  /** While the film builds the scene on screen goes with a change unless
   *  the person leaves it out (its chip): the player still plays it alone. */
  const [sceneLeftOut, setSceneLeftOut] = useState(false);
  /** What the player plays. While the film builds it is kept until the
   *  person picks a scene it does not have yet (every reading would load the
   *  scene on screen again: its links are new each time). */
  const [held, setHeld] = useState<Manifest | null>(null);
  useEffect(() => {
    if (!latest) return;
    setHeld((h) => {
      if (!h || allReady(latest) || h.film_version !== latest.film_version)
        return latest;
      if (!h.scenes.some((s) => s.ready)) return latest;
      const has = (m: Manifest) =>
        !!pick && !!m.scenes.find((s) => s.id === pick)?.ready;
      return has(latest) && !has(h) ? latest : h;
    });
  }, [latest, pick]);
  const manifest = held;
  /** The scene the player opens on, read when the player is made: the one
   *  remembered once the film is whole, else the first built. */
  const opening = useRef<string | null>(null);
  if (held && !opening.current) {
    opening.current = allReady(held)
      ? ad.focus_scene
      : (held.scenes.find((s) => s.ready)?.id ?? null);
  }

  const otherQ = useManifest(
    comparing !== null ? manifestUrlOf(ad.id, comparing) : null,
  );
  const other = otherQ.data ?? null;

  // the film's description: the event's (an instant change) until the
  // current version's own arrives
  const versionFilm = versionQ.data?.film ?? null;
  useEffect(() => setEventFilm(null), [versionFilm]);
  const film: FilmDoc | null = eventFilm ?? versionFilm;
  /** The languages a way out can speak: the film's own, then each sibling
   *  the version holds (its overlays made, P24-T06). */
  const outLanguages = useMemo(() => {
    const own = versionFilm?.languages ?? [];
    const held = (versionQ.data?.languages ?? {}) as Record<
      string,
      { overlays?: unknown } | undefined
    >;
    return own.filter((l, k) => k === 0 || !!held[l]?.overlays);
  }, [versionFilm, versionQ.data]);

  const setOffset = useCallback((o: FreeOffset | null) => {
    offsetRef.current = o;
    setOffsetState(o);
    playerRef.current?.look(o);
    otherRef.current?.look(o);
  }, []);

  /** The person turned the view: what they marked on the picture before
   *  no longer matches it (the object they named still does). */
  const look = (o: FreeOffset) => {
    setOffset(o);
    setArea(null);
    setMark(null);
  };

  // ---- the socket: patches to the player, the film's words ----------------
  useEffect(
    () =>
      bus.add((event) => {
        if (event.type !== "change") return;
        const patches = event.patches as Patch[] | undefined;
        if (patches?.length) playerRef.current?.applyPatches(patches);
        if (event.film) setEventFilm(event.film as FilmDoc);
      }),
    [bus],
  );

  // a new manifest keeps the focused scene by its id (a reorder moves it)
  useEffect(() => {
    const h = playerRef.current;
    if (!manifest || !h || !clock.focus) return;
    if (!manifest.scenes.some((s) => s.id === clock.focus)) h.focus(null);
    else h.focus(clock.focus);
    // only when the manifest changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest]);

  // a scene picked while the film builds plays alone, once the player has it
  useEffect(() => {
    const h = playerRef.current;
    if (!h || !held || !pick || allReady(held)) return;
    if (!held.scenes.find((s) => s.id === pick)?.ready) return;
    h.focus(pick);
    h.play();
  }, [held, pick]);

  // every scene built: the whole film plays from its start
  const wasWhole = useRef<boolean | null>(null);
  useEffect(() => {
    if (!held) return;
    const now = allReady(held);
    const before = wasWhole.current;
    wasWhole.current = now;
    if (before !== false || !now) return;
    setPick(null);
    const h = playerRef.current;
    h?.focus(null);
    h?.seek(0);
    h?.play();
  }, [held]);

  const locked = useCallback(
    (id: string) => !!film?.scenes.find((s) => s.id === id)?.lock,
    [film],
  );
  const scenes: ScrubScene[] = useMemo(
    () => (latest ? scrubScenes(latest, locked) : []),
    [latest, locked],
  );
  const planned: ScrubScene[] = useMemo(
    () =>
      plannedScenes(
        current?.scenes.length ? current.scenes : (ad.brief?.scenes ?? []),
      ),
    [current, ad.brief],
  );
  const indexOf = (id: string) => scenes.findIndex((s) => s.id === id);
  const nameOfObject = (id: string) =>
    film?.cast.find((c) => c.id === id)?.name ?? id;

  // ---- the strip's tiles: frames the player draws in the browser ----------
  const aspect = latest?.aspect ?? ad.brief?.aspect ?? "9:16";
  const [aw, ah] = ratioOf(aspect);
  const tileSize = useMemo(() => {
    const k = (TILE_PX * 2) / Math.max(aw, ah);
    return { width: Math.round(aw * k), height: Math.round(ah * k) };
  }, [aw, ah]);
  const tiles = useSceneFrames(latest, tileSize);

  // ---- compare --------------------------------------------------------------
  const syncOther = useCallback(() => {
    const h = playerRef.current;
    const o = otherRef.current;
    if (!h || !o || !manifest || !other) return;
    const m = h.moment();
    const match = matchScene(manifest, other, m.scene);
    if (!match) {
      setMissing(manifest.scenes.find((s) => s.id === m.scene)?.name ?? "");
      return;
    }
    setMissing(null);
    o.focus(clock.focus ? match.id : null);
    o.seek(match.start + Math.min(m.t, Math.max(0, match.seconds - 1e-3)));
  }, [manifest, other, clock.focus]);
  useEffect(() => {
    if (comparing !== null) syncOther();
  }, [comparing, other, syncOther]);

  const onClock = useCallback(
    (c: PlayerClock) => {
      setClock(c);
      if (comparing !== null) syncOther();
    },
    [comparing, syncOther],
  );

  /** The scene a change is about (§8.2 `focus`). */
  const focusSent = !whole && sceneLeftOut ? null : clock.focus;

  // ---- the changes ------------------------------------------------------------
  const changes: Change[] = useMemo(
    () => changesQ.data?.items ?? [],
    [changesQ.data],
  );
  const running = !!changes[0] && RUNNING.includes(changes[0].state);

  // The template card's slots (P22-T05): the templates' list, read again when
  // the template just made is not in it yet.
  const cardTemplate =
    changes[0]?.state === "applied"
      ? (templateCardOf(changes[0].says)?.templateId ?? null)
      : null;
  const templatesQ = useTemplates(cardTemplate !== null);
  const cardFound = !!templatesQ.data?.some((t) => t.id === cardTemplate);
  const refetchTemplates = templatesQ.refetch;
  const askedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!cardTemplate || cardFound || !templatesQ.isSuccess) return;
    if (askedFor.current === cardTemplate) return;
    askedFor.current = cardTemplate;
    void refetchTemplates();
  }, [cardTemplate, cardFound, templatesQ.isSuccess, refetchTemplates]);
  const templateSlots = (id: string): string[] | null => {
    const template = templatesQ.data?.find((t) => t.id === id);
    if (!template) return null;
    return template.slots
      .map((slot) => {
        const s = (slot ?? {}) as { label?: unknown; $slot?: unknown };
        return typeof s.label === "string"
          ? s.label
          : typeof s.$slot === "string"
            ? s.$slot
            : null;
      })
      .filter((label): label is string => !!label);
  };

  const progress = progressQ.data ?? null;
  const shownProgress =
    progress &&
    progress.stage === "bake" &&
    (!ad.building_version || progress.version === ad.building_version.n)
      ? progress
      : null;

  const pictureMark = (): PictureMark | null =>
    area
      ? { kind: "area", rect: area }
      : mark
        ? { kind: "point", at: mark }
        : null;

  // the change last sent, as sent: its yes (§15.1) sends exactly it again
  const lastBody = useRef<ChangeBody | null>(null);
  const send = async (text: string, confirm = false): Promise<unknown> => {
    const h = playerRef.current;
    setSendError(null);
    setSending(true);
    try {
      let body = lastBody.current;
      if (!confirm || !body || body.text !== text) {
        const m = h?.moment() ?? null;
        const pose = offset ? (h?.view() ?? null) : null;
        let picture: string | null = null;
        try {
          picture = (await h?.snapshot(pictureMark())) ?? null;
        } catch {
          picture = null;
        }
        body = {
          text,
          moment: m ? { scene: m.scene, t: round2(Math.min(20, m.t)) } : null,
          focus: focusSent,
          object,
          area: area ? areaOf(area) : null,
          view: pose && manifest ? changeView(pose, manifest.aspect) : null,
          span,
          picture,
        };
        lastBody.current = body;
      }
      const made = await ask.mutateAsync(
        confirm ? { ...body, confirm: true } : body,
      );
      // a change waiting for the person's yes started nothing: the pointing
      // stays with it
      if (!priceAsk(made)) {
        lastBody.current = null;
        setObject(null);
        setMark(null);
        setArea(null);
        setSpan(null);
      }
      return made;
    } catch (error) {
      // the block with Top up is the box's own (§15.1)
      if (!shortfallOf(error)) setSendError(changeError(error));
      throw error;
    } finally {
      setSending(false);
    }
  };

  const onMove = (sceneId: string, beforeId: string | null) => {
    const k = indexOf(sceneId);
    const moved = scenes[k];
    if (!moved) return;
    const before = beforeId ? scenes[indexOf(beforeId)] : null;
    const text = before
      ? say(SCRUB.moveText, {
          n: k + 1,
          name: moved.name,
          m: indexOf(before.id) + 1,
          other: before.name,
        })
      : say(SCRUB.moveToEnd, { n: k + 1, name: moved.name });
    send(text).catch((error: unknown) => {
      // a move refused for credits says so in the box's line
      if (shortfallOf(error)) setSendError(changeError(error));
    });
  };

  const onFocus = (sceneId: string | null) => {
    if (!whole) {
      // while the film builds a built scene plays alone; there is no film
      // to go back to yet: leaving the scene leaves it out of the change
      if (!sceneId) {
        setSceneLeftOut(true);
        return;
      }
      if (!latest?.scenes.find((s) => s.id === sceneId)?.ready) return;
      setSceneLeftOut(false);
      setPick(sceneId);
      setSpan((s) => (s && s.scene !== sceneId ? null : s));
      return;
    }
    playerRef.current?.focus(sceneId);
    setSpan((s) => (sceneId && s && s.scene !== sceneId ? null : s));
    focusQ.mutate(sceneId);
  };

  // ---- pointing chips -----------------------------------------------------------
  const chips: PointingChip[] = [];
  if (focusSent) {
    const k = indexOf(focusSent);
    if (k >= 0) {
      chips.push({
        key: "focus",
        label: say(BOX.focusChip, { n: k + 1, name: scenes[k].name }),
        onClear: () => onFocus(null),
      });
    }
  }
  if (object) {
    chips.push({
      key: "object",
      label: say(BOX.objectChip, { name: nameOfObject(object) }),
      onClear: () => {
        setObject(null);
        setMark(null);
      },
    });
  }
  if (area) {
    chips.push({
      key: "area",
      label: BOX.areaChip,
      onClear: () => setArea(null),
    });
  }
  if (offset) {
    chips.push({
      key: "view",
      label: BOX.viewChip,
      onClear: () => setOffset(null),
    });
  }
  if (span) {
    const k = indexOf(span.scene);
    chips.push({
      key: "span",
      label: say(BOX.spanChip, {
        t0: secondsText(span.t0),
        t1: secondsText(span.t1),
        n: k + 1,
      }),
      onClear: () => setSpan(null),
    });
  }

  // ---- a true render of the paused moment (§13.2) ------------------------------
  const askMoment = () => {
    const h = playerRef.current;
    if (!h) return;
    h.pause();
    const m = h.moment();
    const k = indexOf(m.scene);
    setAsking({
      scene: m.scene,
      t: m.t,
      n: k + 1,
      name: scenes[k]?.name ?? "",
      free: !!offset,
    });
  };
  const sceneOf = (id: string) => {
    const k = indexOf(id);
    return k >= 0 ? { n: k + 1, name: scenes[k].name } : null;
  };
  const goTo = (m: { scene: string; t: number }) => {
    const h = playerRef.current;
    const scene = manifest?.scenes.find((s) => s.id === m.scene);
    if (!h || !scene) return;
    h.pause();
    if (clock.focus && clock.focus !== m.scene) onFocus(null);
    h.seek(scene.start + Math.min(m.t, Math.max(0, scene.seconds - 1e-3)));
  };

  // ---- the brief, as a fold (§5.5) ----------------------------------------------
  const brief =
    (versionQ.data?.brief as Brief | null | undefined) ?? ad.brief ?? null;
  // the names of the brief's references, as the cast panel says them (P28-T16)
  const castQ = useCast(ad.id);

  // ---- what shows ------------------------------------------------------------
  const total = latest?.scenes.length ?? planned.length;
  const built = latest ? latest.scenes.filter((s) => s.ready).length : 0;
  const progressLine = !current
    ? BUILD.writing
    : !whole
      ? say(BUILD.building, { k: Math.min(built + 1, total), n: total })
      : null;

  const loading = (
    <div
      className="flex w-full flex-col gap-2"
      aria-busy="true"
      data-testid="film-loading"
    >
      <Skeleton height={320} />
      <span className="text-sm text-slate-500">{FILM_STATE.loading}</span>
    </div>
  );

  /** The picture before any scene can play: the film's shape, grey. */
  const waitingPicture = (
    <div
      className="mx-auto w-full animate-pulse rounded-lg bg-slate-200"
      style={{
        aspectRatio: `${aw} / ${ah}`,
        maxWidth: `calc(min(60vh, 42rem) * ${aw / ah})`,
      }}
      data-testid="film-waiting"
      aria-hidden="true"
    />
  );

  const layer = (
    <PictureLayer
      playing={clock.playing}
      lookMode={lookMode}
      area={area}
      mark={mark}
      onLook={(dx, dy, h, from) => look(orbitStep(from, dx, dy, h))}
      onPan={(dx, dy, h, from) =>
        look(
          panStep(from, dx, dy, h, playerRef.current?.filmCamera()?.fovY ?? 35),
        )
      }
      onDolly={(k) => look(dollyStep(offsetRef.current ?? FREE_ZERO, k))}
      offsetNow={() => offsetRef.current ?? FREE_ZERO}
      onArea={(rect) => {
        setArea(rect);
        setMark(null);
        setObject(null);
      }}
      onClick={(x, y) => {
        const id = playerRef.current?.pick(x, y) ?? null;
        setArea(null);
        if (id) {
          setObject(id);
          setMark([x, y]);
          setNotice(null);
        } else {
          setObject(null);
          setMark(null);
          setNotice(LOOK.nothingThere);
        }
      }}
    />
  );

  // a building version answers 409 until its first scene is built: not an error
  const failedToLoad = manifestQ.isError && !building;

  return (
    <div
      className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(20rem,26rem)] lg:grid-rows-[auto_minmax(0,1fr)]"
      data-testid="film-page"
    >
      <div className="flex min-w-0 flex-col gap-3">
        <Card className="flex flex-col gap-3 p-3 md:p-4">
          {failedToLoad ? (
            <div
              className="flex flex-col items-start gap-3 p-2"
              role="alert"
              data-testid="film-error"
            >
              <p className="m-0 text-sm text-slate-700">{FILM_STATE.failed}</p>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => void manifestQ.refetch()}
              >
                {FILM_STATE.retry}
              </Button>
            </div>
          ) : (
            <div
              className={
                comparing !== null ? "grid grid-cols-2 gap-2 md:gap-3" : ""
              }
              data-testid={comparing !== null ? "compare" : undefined}
            >
              {comparing !== null && current ? (
                <p className="col-span-1 m-0 text-xs font-semibold text-slate-600">
                  {say(COMPARE.this, { n: current.n })}
                </p>
              ) : null}
              {comparing !== null ? (
                <p className="col-span-1 m-0 text-xs font-semibold text-slate-600">
                  {say(COMPARE.other, { n: comparing })}
                </p>
              ) : null}
              <div className="min-w-0">
                {manifest ? (
                  <Suspense fallback={loading}>
                    <LazyFilmPlayer
                      ref={playerRef}
                      manifest={manifest}
                      controls={false}
                      autoPlay={!allReady(manifest)}
                      maxHeight="min(60vh, 42rem)"
                      initialFocus={opening.current}
                      onClock={onClock}
                      overlay={layer}
                    />
                  </Suspense>
                ) : building ? (
                  waitingPicture
                ) : (
                  loading
                )}
              </div>
              {comparing !== null ? (
                <div className="min-w-0" data-testid="compare-other">
                  {other ? (
                    <Suspense fallback={loading}>
                      <LazyFilmPlayer
                        ref={(h) => {
                          otherRef.current = h;
                          if (h) {
                            h.look(offsetRef.current);
                            syncOther();
                          }
                        }}
                        manifest={other}
                        controls={false}
                        maxHeight="min(60vh, 42rem)"
                        overlay={
                          missing !== null ? (
                            <div
                              className="absolute inset-0 flex items-center justify-center bg-slate-300 p-3 text-center text-sm text-slate-700"
                              data-testid="compare-missing"
                            >
                              {say(COMPARE.missing, {
                                name: missing,
                                n: comparing,
                              })}
                            </div>
                          ) : null
                        }
                      />
                    </Suspense>
                  ) : otherQ.isError ? (
                    <p role="alert" className="m-0 text-sm text-slate-700">
                      {say(COMPARE.notReady, { n: comparing })}
                    </p>
                  ) : (
                    <p className="m-0 text-sm text-slate-500" role="status">
                      {say(COMPARE.loading, { n: comparing })}
                    </p>
                  )}
                </div>
              ) : null}
            </div>
          )}

          {progressLine ? (
            <div
              className="flex flex-col gap-1.5"
              role="status"
              data-testid="build-progress"
            >
              <p className="m-0 text-sm font-semibold text-slate-900">
                {progressLine}
              </p>
              <div className="h-1.5 w-full overflow-hidden rounded-pill bg-slate-200">
                <div
                  className={`h-full rounded-pill bg-brand transition-[width] ${current ? "" : "animate-pulse"}`}
                  style={{
                    width: `${current && total ? Math.max(4, (built / total) * 100) : 33}%`,
                  }}
                />
              </div>
              <p className="m-0 text-xs text-slate-500">{BUILD.hint}</p>
            </div>
          ) : null}

          {manifest ? (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {!clock.playing ? (
                <Button
                  type="button"
                  size="sm"
                  variant={lookMode ? "primary" : "ghost"}
                  aria-pressed={lookMode}
                  onClick={() => setLookMode((v) => !v)}
                >
                  {LOOK.lookAround}
                </Button>
              ) : null}
              {offset ? (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => setOffset(null)}
                >
                  {LOOK.back}
                </Button>
              ) : null}
              <span className="text-xs text-slate-500" data-testid="look-hint">
                {notice ??
                  (clock.playing || lookMode
                    ? LOOK.hintPlaying
                    : LOOK.hintPaused)}
              </span>
              {/* §13: Approve opens the ways out once the film is ready (the
                  brand kit's line is the page heading's) */}
              {building ? null : (
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  className="ml-auto"
                  aria-expanded={waysOpen}
                  title={WAYS_OUT.approveHint}
                  onClick={() => setWaysOpen((v) => !v)}
                >
                  {WAYS_OUT.approve}
                </Button>
              )}
            </div>
          ) : null}

          {manifest && scenes.length ? (
            <Scrubber
              title={ad.title}
              scenes={scenes}
              tiles={tiles}
              building={!whole}
              focus={clock.focus}
              filmTime={clock.filmTime}
              playing={clock.playing}
              span={span}
              moveDisabled={running || sending || !whole}
              onToggle={() =>
                clock.playing
                  ? playerRef.current?.pause()
                  : playerRef.current?.play()
              }
              onSeek={(t) => playerRef.current?.seek(t)}
              onFocus={onFocus}
              onSpan={setSpan}
              onMove={onMove}
            />
          ) : (
            <PlannedStrip scenes={planned} />
          )}
        </Card>
        {waysOpen && current ? (
          <WaysOut
            adId={ad.id}
            version={current.n}
            languages={outLanguages}
            onClose={() => setWaysOpen(false)}
            onMoment={askMoment}
          />
        ) : null}
      </div>

      {/* the conversation, the cast panel, the brief and the versions: beside
          the player and what was made from it on a wide screen, right after
          the player on a phone */}
      <div className="flex min-w-0 flex-col gap-4 lg:row-span-2">
        <ChangeBox
          changes={changes}
          progress={shownProgress}
          chips={chips}
          waiting={building}
          closed={current ? null : BUILD.boxWriting}
          sending={sending || answer.isPending || confirm.isPending}
          error={
            sendError ??
            (answer.error
              ? changeError(answer.error)
              : confirm.error
                ? changeError(confirm.error)
                : cancel.error
                  ? changeError(cancel.error)
                  : null)
          }
          onSend={send}
          onStop={(n) => cancel.mutate(n)}
          onAnswer={(n, a: ChangeAnswer) => answer.mutate({ n, answer: a })}
          onConfirm={(n) => confirm.mutate(n)}
          templateSlots={templateSlots}
        />
        {/* the cast panel's place (§6.3, P28-T14) */}
        {cast ? (
          <div id="film-objects" data-testid="cast-slot">
            {/* a stand-in's picture is drawn from the film it is in */}
            <CastFilmContext.Provider value={latest}>
              {cast}
            </CastFilmContext.Provider>
          </div>
        ) : null}
        {brief ? (
          <details
            className="tb-card tb-card-plain group overflow-hidden"
            data-testid="brief-fold"
          >
            <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between gap-3 px-5 py-3 text-sm font-semibold text-slate-900">
              <span>{BUILD.briefFold}</span>
              <span
                aria-hidden="true"
                className="text-slate-400 transition-transform group-open:rotate-180"
              >
                ▾
              </span>
            </summary>
            <div className="flex flex-col gap-2 border-t border-slate-100 pb-2">
              <p className="m-0 px-5 pt-3 text-xs text-slate-500">
                {BUILD.briefFoldHint}
              </p>
              <BriefCard
                brief={brief}
                kitName={ad.brand_kit_name}
                editable={!!current && !running && !sending}
                final
                afterFilm
                compact
                busy={edit.isPending || accept.isPending}
                onEdit={(field, value) => edit.mutateAsync({ field, value })}
                onAccept={(index, value) => accept.mutate({ index, value })}
                refused={accept.error}
                onYouDecide={() => undefined}
                dropKey={`truereel:dropped:${ad.id}`}
                names={refNames(brief, castQ.data?.rows ?? [])}
              />
              {edit.error ? (
                <p role="alert" className="m-0 px-5 text-sm text-slate-700">
                  {sentenceFor(edit.error)}
                </p>
              ) : null}
            </div>
          </details>
        ) : null}
        <VersionsTree
          versions={versionsQ.data ?? []}
          comparing={comparing}
          busy={running || restore.isPending || building}
          error={restore.error ? changeError(restore.error) : null}
          onRestore={(n) => restore.mutate(n)}
          onCompare={(n) => {
            if (n !== null) playerRef.current?.pause();
            setMissing(null);
            setComparing(n);
          }}
        />
      </div>
      {!building ? (
        <div
          className="flex min-w-0 flex-col gap-4 lg:col-start-1"
          data-testid="made"
        >
          <Outputs adId={ad.id} />
          <TrueRenderPanel
            adId={ad.id}
            playing={clock.playing}
            asking={asking}
            onAsk={askMoment}
            onAskClose={() => setAsking(null)}
            sceneOf={sceneOf}
            onGoTo={goTo}
          />
          <ShareCard adId={ad.id} />
        </div>
      ) : null}
    </div>
  );
}
